/**
 * Ошибка запроса с сохранёнными МАШИННЫМ кодом сервера и HTTP-статусом.
 *
 * Роуты тегов/комментариев (и любые новые роуты того же стиля) отдают в теле
 * не текст для человека, а код: `{ error: 'not_found' }`, `{ error:
 * 'invalid_name' }`, `{ error: 'forbidden' }`. Голый `unwrap` фабрики
 * `createModuleApi` (`errorFormat: 'raw'`) склеивает его в строку
 * `request failed 400: {…}` — и ровно эта строка уезжала бы в тост.
 *
 * Код нужен ОТДЕЛЬНЫМ ПОЛЕМ, чтобы поверхность подобрала русскую фразу по
 * своей карте (`Record<code, string>`); статус — чтобы различать исходы там,
 * где карта не по коду (409 на переименование тега = ошибка ПОЛЯ, а не тост).
 * `message` равен коду (либо `http_<status>`), дословно его не показывают.
 *
 * Один класс на все `admin-module-*`: раньше `admin-module-conversations` и
 * `admin-module-annotations` держали по своей копии (`AnnotationRequestError`
 * / `AnnotationsRequestError`), которые расходились в мелочах — одна хранила
 * код в `code`, другая в `message`, — и `instanceof` из одного модуля не
 * узнавал ошибку другого.
 */
export class ModuleRequestError extends Error {
    code;
    status;
    /**
     * Машинный код ДВИЖКА/источника за шлюзом admin-api (`body.code`): у ответа
     * `{ error: 'upstream_rejected', code: 'social_not_enabled', message: '…' }`
     * поле `code` класса равно `upstream_rejected`, а различать экраны
     * («модуль выключен» против «такое действие уже есть») можно только по
     * этому полю. null — в теле его нет (у большинства роутов).
     */
    upstreamCode;
    /**
     * Готовая русская фраза admin-api для человека (`body.message`); null — в
     * теле её нет. НЕ путать с `Error.message`: тот по-прежнему равен коду
     * (контракт класса до расширения — на него уже опираются экраны).
     */
    serverMessage;
    /**
     * `body.issues` как есть (сырой массив, `[]` — нет): проблемы значений по
     * полям формы. Форма элемента — контракт раздела, разбирает модуль своей
     * схемой (например, `adsIssuesOf` в «Запусках FB»).
     */
    issues;
    /**
     * Остальные поля отказа: вложенный `body.details` поверх прочих полей
     * верхнего уровня (`currentVersion`, `batchId`, `previewHash`, `writing`…);
     * `Retry-After` ответа — как `retryAfterSec`. `{}` — нет.
     */
    details;
    constructor(code, status, extra = {}) {
        super(code);
        this.code = code;
        this.status = status;
        this.name = 'ModuleRequestError';
        this.upstreamCode = extra.upstreamCode ?? null;
        this.serverMessage = extra.serverMessage ?? null;
        this.issues = extra.issues ?? [];
        this.details = extra.details ?? {};
    }
}
/**
 * `issues` и `details` из уже разобранного тела ошибки (см. поля
 * `ModuleRequestError`). Только для тел `{ error: '<строка>', … }`: у тела
 * `zValidator` поля чужие. `retryAfter` — заголовок `Retry-After` в секундах.
 */
export function errorExtrasOf(data, retryAfter) {
    let issues = [];
    let details = {};
    if (data != null && typeof data === 'object' && !Array.isArray(data)) {
        const body = data;
        if (typeof body.error === 'string') {
            if (Array.isArray(body.issues))
                issues = body.issues;
            const { error: _e, code: _c, message: _m, issues: _i, details: nested, success: _s, ...top } = body;
            details = {
                ...top,
                ...(nested && typeof nested === 'object' && !Array.isArray(nested)
                    ? nested
                    : {}),
            };
        }
    }
    const ra = retryAfter != null ? Number(retryAfter) : NaN;
    if (Number.isFinite(ra) && ra > 0 && details.retryAfterSec === undefined) {
        details = { ...details, retryAfterSec: ra };
    }
    return { issues, details };
}
const nonEmptyString = (v) => typeof v === 'string' && v.length > 0 ? v : null;
/**
 * Всё, что экрану нужно из ошибочного ответа, одним чтением тела (тело
 * `Response` читается один раз).
 *
 * `code` — по тем же правилам, что `readErrorCode`; `upstreamCode` и
 * `serverMessage` берутся только из `{ error: '<строка>', … }` — у тела
 * `zValidator` (`{ success: false, error: ZodError }`) поле `message` чужое
 * (текст zod по-английски), и показывать его человеку нельзя.
 */
export async function readErrorBody(r) {
    return errorBodyOf(await readJsonOrUndefined(r), r.status);
}
const readJsonOrUndefined = async (r) => {
    try {
        return await r.json();
    }
    catch {
        return undefined; // тело не JSON — код по статусу
    }
};
function errorBodyOf(raw, status) {
    const data = raw;
    if (data != null && typeof data === 'object') {
        if (typeof data.error === 'string') {
            return {
                code: data.error,
                upstreamCode: nonEmptyString(data.code),
                serverMessage: nonEmptyString(data.message),
            };
        }
        if (data.success === false) {
            return { code: 'invalid_body', upstreamCode: null, serverMessage: null };
        }
    }
    return { code: `http_${status}`, upstreamCode: null, serverMessage: null };
}
/**
 * `ModuleRequestError` из ошибочного ответа (одно чтение тела): код, код
 * апстрима, фраза, `issues`, `details` (+ `retryAfterSec` из `Retry-After`).
 */
export async function moduleRequestErrorFrom(r) {
    const data = await readJsonOrUndefined(r);
    const body = errorBodyOf(data, r.status);
    return new ModuleRequestError(body.code, r.status, {
        upstreamCode: body.upstreamCode,
        serverMessage: body.serverMessage,
        ...errorExtrasOf(data, r.headers.get('retry-after')),
    });
}
/**
 * Машинный код ошибки из ответа.
 *
 * - `{ error: '<code>' }` — код как есть (роуты пишут именно так);
 * - `{ success: false, error: ZodError }` — так `zValidator` отвечает на
 *   невалидное тело: `error` там объект, не строка, и это ровно «тело не
 *   прошло контракт», то есть `invalid_body`;
 * - тело не JSON (или JSON без кода) — `http_<status>`. НЕ `statusText`: это
 *   не код, у карт ошибок по нему ключей нет, а прокси/браузер отдают его
 *   по-разному (у HTTP/2 его нет вовсе).
 */
export async function readErrorCode(r) {
    return (await readErrorBody(r)).code;
}
/**
 * Не-2xx → `ModuleRequestError` с кодом из тела (и `upstreamCode` /
 * `serverMessage`, если они там есть), 2xx → `r.json()`.
 *
 * Для ответов БЕЗ тела (204) не годится — `r.json()` упадёт на успехе; такие
 * вызовы проверяют `r.ok` сами и бросают `ModuleRequestError` напрямую.
 */
export async function unwrapCoded(r) {
    if (!r.ok)
        throw await moduleRequestErrorFrom(r);
    return (await r.json());
}
//# sourceMappingURL=request-error.js.map